/**
 * A chapter's number as a theme shows it (part 05, table T, T14, T″2; part 14, F12): what the preview, PDF, EPUB and DOCX
 * set above the title. Number words are the book language's, up to {@link MAX_NUMBER_WORD}; past that the figure, as a
 * table of every number a book could reach isn't worth its weight (decision 78). A native speaker should check them
 * before launch, as the matter's words (part 05 log).
 */
import { at } from '../arrays.ts';
import type { BookLanguage } from '../theme/metadata.ts';
import type { ChapterNumbering } from '../theme/theme.ts';

/** F12: a chapter number in Roman figures. */
export function romanNumeral(n: number): string {
  const figures: readonly (readonly [number, string])[] = [
    [1000, 'M'],
    [900, 'CM'],
    [500, 'D'],
    [400, 'CD'],
    [100, 'C'],
    [90, 'XC'],
    [50, 'L'],
    [40, 'XL'],
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
  ];
  let left = n;
  let out = '';
  for (const [value, figure] of figures) {
    for (; left >= value; left -= value) out += figure;
  }
  return out;
}

/** The last number written as a word; a higher one stays a figure. */
export const MAX_NUMBER_WORD = 99;

/** How a language says 1–99: the words below twenty, the tens from twenty, and how a ten joins a unit. */
interface NumberWords {
  readonly small: readonly string[];
  readonly tens: readonly string[];
  readonly join: (ten: string, unit: string) => string;
  /** A word this language says on its own, not as `join` builds it. */
  readonly special?: Readonly<Record<number, string>>;
  /** What "Chapter" is. */
  readonly chapter: string;
  /** English capitalises every part of "Chapter Twenty-One"; the rest write the number as a common word after "Kapittel". */
  readonly titleCase?: boolean;
}

const list = (words: string): readonly string[] => words.split(' ');
const glued = (ten: string, unit: string): string => `${ten}${unit}`;
const spaced = (ten: string, unit: string): string => `${ten} ${unit}`;
const unitFirst = (and: string) => (ten: string, unit: string): string => `${unit}${and}${ten}`;

const NORDIC_SMALL = 'to tre fire fem seks sju åtte ni ti elleve tolv tretten fjorten femten seksten sytten atten nitten';
const NORDIC_TENS = list('tjue tretti førti femti seksti sytti åtti nitti');
const ENGLISH: NumberWords = {
  small: list('one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen'),
  tens: list('twenty thirty forty fifty sixty seventy eighty ninety'),
  join: (ten, unit) => `${ten}-${unit}`,
  chapter: 'Chapter',
  titleCase: true,
};
const SPANISH_TWENTIES = list('veintiuno veintidós veintitrés veinticuatro veinticinco veintiséis veintisiete veintiocho veintinueve');
const FRENCH_SEVENTIES = list('soixante-dix soixante-et-onze soixante-douze soixante-treize soixante-quatorze soixante-quinze soixante-seize soixante-dix-sept soixante-dix-huit soixante-dix-neuf');
const FRENCH_EIGHTIES = list('quatre-vingts quatre-vingt-un quatre-vingt-deux quatre-vingt-trois quatre-vingt-quatre quatre-vingt-cinq quatre-vingt-six quatre-vingt-sept quatre-vingt-huit quatre-vingt-neuf');
const FRENCH_NINETIES = list('quatre-vingt-dix quatre-vingt-onze quatre-vingt-douze quatre-vingt-treize quatre-vingt-quatorze quatre-vingt-quinze quatre-vingt-seize quatre-vingt-dix-sept quatre-vingt-dix-huit quatre-vingt-dix-neuf');
const run = (from: number, words: readonly string[]): Record<number, string> => Object.fromEntries(words.map((word, i) => [from + i, word]));

const WORDS: Readonly<Record<BookLanguage, NumberWords>> = {
  'en-US': ENGLISH,
  'en-GB': ENGLISH,
  de: {
    small: list('eins zwei drei vier fünf sechs sieben acht neun zehn elf zwölf dreizehn vierzehn fünfzehn sechzehn siebzehn achtzehn neunzehn'),
    tens: list('zwanzig dreißig vierzig fünfzig sechzig siebzig achtzig neunzig'),
    join: (ten, unit) => `${unit === 'eins' ? 'ein' : unit}und${ten}`,
    chapter: 'Kapitel',
  },
  fr: {
    small: list('un deux trois quatre cinq six sept huit neuf dix onze douze treize quatorze quinze seize dix-sept dix-huit dix-neuf'),
    tens: list('vingt trente quarante cinquante soixante'),
    join: (ten, unit) => (unit === 'un' ? `${ten}-et-un` : `${ten}-${unit}`),
    special: { ...run(70, FRENCH_SEVENTIES), ...run(80, FRENCH_EIGHTIES), ...run(90, FRENCH_NINETIES) },
    chapter: 'Chapitre',
  },
  es: {
    small: list('uno dos tres cuatro cinco seis siete ocho nueve diez once doce trece catorce quince dieciséis diecisiete dieciocho diecinueve'),
    tens: list('veinte treinta cuarenta cincuenta sesenta setenta ochenta noventa'),
    join: (ten, unit) => `${ten} y ${unit}`,
    special: run(21, SPANISH_TWENTIES),
    chapter: 'Capítulo',
  },
  it: {
    small: list('uno due tre quattro cinque sei sette otto nove dieci undici dodici tredici quattordici quindici sedici diciassette diciotto diciannove'),
    tens: list('venti trenta quaranta cinquanta sessanta settanta ottanta novanta'),
    join: (ten, unit) => (unit === 'uno' || unit === 'otto' ? `${ten.slice(0, -1)}${unit}` : `${ten}${unit === 'tre' ? 'tré' : unit}`),
    chapter: 'Capitolo',
  },
  pt: {
    small: list('um dois três quatro cinco seis sete oito nove dez onze doze treze catorze quinze dezasseis dezassete dezoito dezanove'),
    tens: list('vinte trinta quarenta cinquenta sessenta setenta oitenta noventa'),
    join: (ten, unit) => `${ten} e ${unit}`,
    chapter: 'Capítulo',
  },
  nl: {
    small: list('een twee drie vier vijf zes zeven acht negen tien elf twaalf dertien veertien vijftien zestien zeventien achttien negentien'),
    tens: list('twintig dertig veertig vijftig zestig zeventig tachtig negentig'),
    join: (ten, unit) => `${unit}${unit.endsWith('e') ? 'ën' : 'en'}${ten}`,
    chapter: 'Hoofdstuk',
  },
  sv: {
    small: list('ett två tre fyra fem sex sju åtta nio tio elva tolv tretton fjorton femton sexton sjutton arton nitton'),
    tens: list('tjugo trettio fyrtio femtio sextio sjuttio åttio nittio'),
    join: glued,
    chapter: 'Kapitel',
  },
  nb: { small: list(`en ${NORDIC_SMALL}`), tens: NORDIC_TENS, join: glued, chapter: 'Kapittel' },
  nn: { small: list(`ein ${NORDIC_SMALL}`), tens: NORDIC_TENS, join: glued, chapter: 'Kapittel' },
  da: {
    small: list('en to tre fire fem seks syv otte ni ti elleve tolv tretten fjorten femten seksten sytten atten nitten'),
    tens: list('tyve tredive fyrre halvtreds tres halvfjerds firs halvfems'),
    join: unitFirst('og'),
    chapter: 'Kapitel',
  },
  fi: {
    small: list('yksi kaksi kolme neljä viisi kuusi seitsemän kahdeksan yhdeksän kymmenen yksitoista kaksitoista kolmetoista neljätoista viisitoista kuusitoista seitsemäntoista kahdeksantoista yhdeksäntoista'),
    tens: list('kaksikymmentä kolmekymmentä neljäkymmentä viisikymmentä kuusikymmentä seitsemänkymmentä kahdeksankymmentä yhdeksänkymmentä'),
    join: glued,
    chapter: 'Luku',
  },
  pl: {
    small: list('jeden dwa trzy cztery pięć sześć siedem osiem dziewięć dziesięć jedenaście dwanaście trzynaście czternaście piętnaście szesnaście siedemnaście osiemnaście dziewiętnaście'),
    tens: list('dwadzieścia trzydzieści czterdzieści pięćdziesiąt sześćdziesiąt siedemdziesiąt osiemdziesiąt dziewięćdziesiąt'),
    join: spaced,
    chapter: 'Rozdział',
  },
  cs: {
    small: list('jedna dvě tři čtyři pět šest sedm osm devět deset jedenáct dvanáct třináct čtrnáct patnáct šestnáct sedmnáct osmnáct devatenáct'),
    tens: list('dvacet třicet čtyřicet padesát šedesát sedmdesát osmdesát devadesát'),
    join: spaced,
    chapter: 'Kapitola',
  },
  ca: {
    small: list('un dos tres quatre cinc sis set vuit nou deu onze dotze tretze catorze quinze setze disset divuit dinou'),
    tens: list('vint trenta quaranta cinquanta seixanta setanta vuitanta noranta'),
    join: (ten, unit) => (ten === 'vint' ? `${ten}-i-${unit}` : `${ten}-${unit}`),
    chapter: 'Capítol',
  },
};

/** T″2: `n` as a lower-case word in `language`, or its figure past {@link MAX_NUMBER_WORD}. */
export function numberWord(n: number, language: BookLanguage): string {
  if (n > MAX_NUMBER_WORD) return String(n);
  const words = WORDS[language];
  const special = words.special?.[n];
  if (special !== undefined) return special;
  const small = words.small[n - 1];
  if (small !== undefined) return small;
  const ten = at(words.tens, Math.floor(n / 10) - 2);
  const unit = words.small[(n % 10) - 1];
  return unit === undefined ? ten : words.join(ten, unit);
}

const capitalised = (word: string, language: BookLanguage): string => `${word.charAt(0).toLocaleUpperCase(language)}${word.slice(1)}`;

/** T″2: what a theme's `numbering` shows above chapter `n`; empty for `none`. */
export function chapterLabel(numbering: ChapterNumbering, n: number, language: BookLanguage): string {
  const words = WORDS[language];
  const word = numberWord(n, language);
  const titled = word.split('-').map((part) => capitalised(part, language)).join('-');
  switch (numbering) {
    case 'none':
      return '';
    case 'arabic':
      return String(n);
    case 'roman':
      return romanNumeral(n);
    case 'word':
      return words.titleCase === true ? titled : capitalised(word, language);
    case 'chapter-word':
      return `${words.chapter} ${words.titleCase === true ? titled : word}`;
    case 'chapter-arabic':
      return `${words.chapter} ${String(n)}`;
  }
}
